import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import orientationService from '../../../api/services/orientationService';
import { Calendar, Clock, MapPin, CheckCircle, XCircle } from 'lucide-react';

const OrientationInviteResponse = () => {
  const { inviteId, action } = useParams();
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [invitation, setInvitation] = useState(null);
  const [orientation, setOrientation] = useState(null);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  // Load invitation details
  const loadInvitation = async (showLoading = true) => {
    try {
      if (showLoading) {
        setLoading(true);
      }
      setError(null);

      const result = await orientationService.getInvitationDetails(inviteId);

      if (result && result.responseCode === 200 && result.data) {
        setInvitation(result.data.invite);
        setOrientation(result.data.orientation);
        return result.data;
      } else {
        setError('Invitation not found');
        return null;
      }
    } catch (err) {

      setError(err?.response?.data?.responseMessage || err?.response?.data?.message || 'Failed to load invitation');
      return null;
    } finally {
      if (showLoading) {
        setLoading(false);
      }
    }
  };

  const showAlreadyRespondedMessage = (status) => {
    if (status === 'accepted') {
      setSuccessMessage('You have already accepted this invitation.');
      setError(null);
    } else if (status === 'declined') {
      setError('You have already declined this invitation.');
      setSuccessMessage(null);
    }
  };

  const attemptInvitationAction = async (actionType, statusOverride = null) => {
    const currentStatus = (statusOverride || invitation?.status || '').toLowerCase();
    if (currentStatus === 'accepted' || currentStatus === 'declined') {
      showAlreadyRespondedMessage(currentStatus);
      return;
    }
    await handleAction(actionType);
  };

  const handleAction = async (actionType) => {
    try {
      setProcessing(true);
      setError(null);
      setSuccessMessage(null);

      let response;
      if (actionType === 'accept') {
        response = await orientationService.acceptInvitation(inviteId);
      } else {
        response = await orientationService.declineInvitation(inviteId);
      }

      // Reload invitation to get updated status (without showing loading screen)
      await loadInvitation(false);

      setSuccessMessage(
        actionType === 'accept'
          ? 'You have successfully accepted the orientation invitation!'
          : 'You have declined the orientation invitation.'
      );
    } catch (err) {
      const apiMsg = err?.response?.data?.responseMessage || err?.response?.data?.message || '';
      setError(apiMsg || `Unable to ${actionType} the invitation.`);

      // Even if action fails, load invitation details to show current status (without showing loading screen)
      await loadInvitation(false);
    } finally {
      setProcessing(false);
    }
  };

  // Load invitation details
  useEffect(() => {
    const initialize = async () => {
      // Always load invitation details first
      const inviteData = await loadInvitation();

      // If action is provided in URL, process it after loading completes
      if (action && (action === 'accept' || action === 'decline')) {
        await attemptInvitationAction(action, inviteData?.invite?.status);
      }
    };

    initialize();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inviteId, action]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="max-w-2xl w-full bg-white shadow-lg rounded-lg p-8 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0F2942] mx-auto mb-4"></div>
          <p className="text-gray-600">Loading invitation details...</p>
        </div>
      </div>
    );
  }

  if (error && !invitation) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="max-w-2xl w-full bg-white shadow-lg rounded-lg p-8 text-center">
          <div className="text-4xl mb-4">⚠️</div>
          <h1 className="text-2xl font-semibold text-gray-900 mb-2">Error</h1>
          <p className="text-gray-700 mb-4">{error}</p>
        </div>
      </div>
    );
  }

  const isAccepted = invitation?.status === 'accepted';
  const isDeclined = invitation?.status === 'declined';
  const canAccept = !isAccepted && !isDeclined && !processing;
  const canDecline = !isAccepted && !isDeclined && !processing;

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-3xl mx-auto">
        {/* Success Message */}
        {successMessage && (
          <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4 flex items-center space-x-3">
            <CheckCircle className="h-6 w-6 text-green-600" />
            <p className="text-green-800 font-medium">{successMessage}</p>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-center space-x-3">
            <XCircle className="h-6 w-6 text-red-600" />
            <p className="text-red-800 font-medium">{error}</p>
          </div>
        )}

        {/* Invitation Card */}
        <div className="bg-white shadow-lg rounded-lg overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-6">
            <h1 className="text-2xl font-bold mb-2">Orientation Invitation</h1>
            <p className="text-blue-100">
              {isAccepted && '✅ You have accepted this invitation'}
              {isDeclined && '❌ You have declined this invitation'}
              {!isAccepted && !isDeclined && 'Please confirm your attendance'}
            </p>
          </div>

          {/* Orientation Details */}
          {orientation && (
            <div className="p-6 space-y-6">
              {/* Title and Description */}
              <div>
                <h2 className="text-xl font-semibold text-gray-900 mb-2 break-words">
                  {orientation.title}
                </h2>
                {orientation.description && (
                  <p className="text-gray-600 break-words whitespace-pre-wrap">
                    {orientation.description}
                  </p>
                )}
              </div>

              {/* Details Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-start space-x-3">
                  <Calendar className="h-5 w-5 text-gray-400 mt-1" />
                  <div>
                    <p className="text-sm font-medium text-gray-500">Date</p>
                    <p className="text-gray-900 break-words">
                      {new Date(orientation.date).toLocaleDateString('en-US', {
                        weekday: 'long',
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <Clock className="h-5 w-5 text-gray-400 mt-1" />
                  <div>
                    <p className="text-sm font-medium text-gray-500">Time</p>
                    <p className="text-gray-900 break-words">{orientation.time}</p>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <Clock className="h-5 w-5 text-gray-400 mt-1" />
                  <div>
                    <p className="text-sm font-medium text-gray-500">Duration</p>
                    <p className="text-gray-900 break-words">{orientation.duration} hours</p>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <MapPin className="h-5 w-5 text-gray-400 mt-1 flex-shrink-0" />
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-500">Location</p>
                    <p className="text-gray-900 break-words whitespace-pre-wrap min-w-0">
                      {orientation.location}
                    </p>
                  </div>
                </div>
              </div>

              {/* Additional Info */}
              {(orientation.trainer || orientation.department) && (
                <div className="pt-4 border-t border-gray-200">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {orientation.trainer && (
                      <div>
                        <p className="text-sm font-medium text-gray-500">Trainer</p>
                        <p className="text-gray-900 break-words">{orientation.trainer}</p>
                      </div>
                    )}
                    {orientation.department && (
                      <div>
                        <p className="text-sm font-medium text-gray-500">Department</p>
                        <p className="text-gray-900 break-words">{orientation.department}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="pt-6 border-t border-gray-200">
                {/* Status Message */}
                {(isAccepted || isDeclined) && (
                  <div className={`mt-4 p-4 rounded-lg ${isAccepted ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'
                    }`}>
                    <p className={`text-sm ${isAccepted ? 'text-green-800' : 'text-red-800'
                      }`}>
                      {isAccepted && (
                        <>
                          <strong>You have accepted this invitation.</strong> Please make sure to attend the orientation session on time.
                          If you have any questions, please contact the HR department.
                        </>
                      )}
                      {isDeclined && (
                        <>
                          <strong>You have declined this invitation.</strong> If you change your mind, please contact the HR department
                          to discuss your availability for future orientation sessions.
                        </>
                      )}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default OrientationInviteResponse;
